import {Deal} from "platform/protocol/trading/Deal";
import {Trade} from "core/redux/trades/TradesReduxState";
import {Exposure} from "protocol/trade/Exposure";
import {PositionDirection} from "platform/protocol/enum/PositionDirection";
import {ActionFromData} from "protocol/account/ActionFromData";
import {DealForm} from "core/redux/deal/DealReduxState";
import Platform from "platform/Platform";
import {StoreState} from "core/redux/StoreState";
import {Quote} from "platform/protocol/trading/Quote";
import {AccountState} from "core/state/AccountState";
import {ServiceType} from "enum/ServiceType";
import Utils from "platform/util/Utils";
import {PortfolioCounter} from "core/redux/portfolio/PortfolioReduxState";
import {TradeType} from "protocol/trade/TradeType";
import {OpenDealResponse} from "protocol/trade/OpenDealResponse";
import {TSMap} from "typescript-map";
import {StorageKey} from "enum/StorageKey";
import {Preference} from "core/util/Preference";
import {PlaySound} from "platform/redux/audio/AudioActions";

export class TradeUtil {

    public static mapDeal = (deal: Deal): Trade => {
        const trade: Trade = {
            Id: deal.Id,
            SymbolId: deal.SymbolId,
            SymbolDisplayName: deal.SymbolDisplayName,
            Amount: deal.Amount,
            PositionDirections: [deal.PositionDirection],
            PL: deal.PL,
            PositionPromotions: [...deal.PositionPromotions],
            StopLossRate: deal.StopLossRate,
            StopLossAmount: deal.StopLossAmount,
            TakeProfitRate: deal.TakeProfitRate,
            TakeProfitAmount: deal.TakeProfitAmount,
            Date: deal.Date,
            Rate: deal.Rate,
            MarketRate: deal.MarketRate,
            trades: null
        };
        return trade;
    }

    public static mapExposure = (exposure: Exposure): Trade => {
        const trade: Trade = {
            Id: null,
            SymbolId: exposure.SymbolId,
            SymbolDisplayName: exposure.SymbolDisplayName,
            Amount: exposure.NetValue,
            PositionDirections: [],
            PL: exposure.OpenPL,
            PositionPromotions: [],
            StopLossRate: null,
            StopLossAmount: null,
            TakeProfitRate: null,
            TakeProfitAmount: null,
            MarketRate: null,
            Date: null,
            Rate: exposure.AverageOpenRate,
            trades: []
        };
        return trade;
    }

    public static recalculateExposure = (exposureTrade: Trade, filteredTrades: Trade[]): Trade => {
        const exposure: Trade = {
            ...exposureTrade,
            Amount: 0,
            PL: 0,
            PositionDirections: [],
            trades: filteredTrades
        };
        filteredTrades.forEach((trade: Trade) => {
            const direction: PositionDirection = trade.PositionDirections[0];
            if (exposure.PositionDirections.indexOf(direction) < 0) {
                exposure.PositionDirections.push(direction);
            }
            if (direction === PositionDirection.Buy) {
                exposure.Amount += trade.Amount;
            } else {
                exposure.Amount -= trade.Amount;
            }
            exposure.PL += trade.PL;
        });
        return exposure;
    }

    public static createDate = (trade: Trade): number => {
        if (!trade.Date) {
            let maxDate: number = null;
            if (Utils.isArrayNotEmpty(trade.trades)) {
                trade.trades.forEach((t: Trade) => {
                    maxDate = Math.max(maxDate, t.Date);
                });
            }
            return maxDate;
        }
        return trade.Date;
    }

    public static mapActionFormData = (): ActionFromData => {
        const accountState: AccountState = Platform.state(ServiceType.Account);
        const {quotes, deal} = Platform.reduxState<StoreState>();
        const form: DealForm = deal.form;
        if (form) {
            const isEntryOrder: boolean = form.RateRequestType === TradeType.EntryOrder;
            const quote: Quote = quotes.quotes.get(form.SymbolId);
            // NOTE: can't send action form data without quote ID
            if (quote) {
                return {
                    AccountId: accountState.accountId.toString(),
                    PositionId: form.PositionId,
                    QuoteId: quote.Id,
                    SymbolId: form.SymbolId,
                    Amount: form.Amount ? form.Amount.toString() : "0",
                    OCORate: null,
                    OrderRate: isEntryOrder ? form.OrderRate : null,
                    PositionDirection: form.PositionDirection,
                    RateRequestType: form.RateRequestType,
                    StopLossValueAmount: Utils.isNull(form.HasUserEnteredStopLossAmount) || form.HasUserEnteredStopLossAmount ? form.StopLossValueAmount : null,
                    StopLossValueRate: Utils.isNull(form.HasUserEnteredStopLossAmount) || !form.HasUserEnteredStopLossAmount ? form.StopLossValueRate : null,
                    TakeProfitValueAmount: Utils.isNull(form.HasUserEnteredTakeProfitAmount) || form.HasUserEnteredTakeProfitAmount ? form.TakeProfitValueAmount : null,
                    TakeProfitValueRate: Utils.isNull(form.HasUserEnteredTakeProfitAmount) || !form.HasUserEnteredTakeProfitAmount ? form.TakeProfitValueRate : null
                };
            }
        }
        return null;
    }

    public static formatCounter(counter: PortfolioCounter): string {
        if (counter) {
            if (Utils.isNotNull(counter.filterCount) && Utils.isNotNull(counter.count) && counter.filterCount < counter.count) {
                return " (" + counter.filterCount + "/" + counter.count + ")";
            } else if (counter.count) {
                return " (" + counter.count + ")";
            }
        }
        return "";
    };

    public static mapResponseToDeal = (response: OpenDealResponse): Deal => {
        if (response) {
            let direction: PositionDirection;
            let openRate: number;
            let stopLoss: number;
            let takeProfit: number;

            if (response.P3 === 1) { // if true this is Buy and openRate is in property P33
                direction = PositionDirection.Buy;
                openRate = response.P33;
            } else { // else this is Sell and openRate is in property P32
                direction = PositionDirection.Sell;
                openRate = response.P32;
            }
            // If P19 is null there is no SL
            if (response.P19) {
                // There is SL if this is true
                if (response.P19.P15) {
                    stopLoss = response.P19.P15.P3;
                    if (response.P18) {
                        takeProfit = response.P18.P15.P3;
                    }
                } else { // If P19 is an empty object it means we have both TP&SL
                    stopLoss = response.P18.P21.P15.P3;
                    takeProfit = response.P18.P15.P3;
                }
            } else {
                if (response.P18) {
                    takeProfit = response.P18.P15.P3;
                }
            }

            const deal: Deal = {
                Id: response.P1, // id -> P1
                Amount: response.P7, // amount -> P7
                PositionDirection: direction, // direction -> P3
                Rate: openRate, // openRate/marketRate(switched in some cases) -> P32/P33
                StopLossRate: stopLoss,
                TakeProfitRate: takeProfit,
                SymbolId: response.P4 // symbolId -> P4
            };
            return deal;
        }
        return {};
    }

    public static playPopupSound = (): void => {
        const preferences: TSMap<StorageKey, any> = Platform.reduxState<StoreState>().settings.preferences;
        const popupSound: Preference<boolean> = preferences.get(StorageKey.PopupSound);
        if (Utils.isNull(popupSound) || popupSound.value) {
            Platform.dispatch(PlaySound({name: "audio/pop_up_sound.mp3"}));
        }
    }
}
